import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import HeroCarousel from "./hero-carousel";
import MediaWithArtDirection from "./media-with-art-direction";

const HeroWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const BigHero = styled(HeroWrapper)`
  height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 430px;
  }
`;
const FrontpageHero = styled(HeroWrapper)`
  height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 90vh;
  }
`;
const SmHero = styled(HeroWrapper)`
  height: 368px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 195px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: clamp(0.5rem, 10vw, 100px) clamp(0.5rem, 7vw, 70px);
  color: var(--color-white);
  h1 {
    font-weight: 300;
  }
  h2 {
    font-weight: 600;
  }
  p {
    font-weight: 600;
    margin: 0;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    a {
      width: 100%;
      margin: 5px 0;
      max-width: 500px;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: 100%;
    justify-content: center;
  }
  letter-spacing: ${({ $letterSpacing }) => $letterSpacing + "px"};
  line-height: ${({ $rowSpacing }) => $rowSpacing + "px"};
`;

const SideBySideContent = styled.div`
  width: 100%;
  color: #000;
  background-color: #ededed;
  padding: 3rem;
  min-height: min(50vh, 30rem);
  text-align: center;
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.colors.secondary};
  color: ${({ textColor, theme }) => textColor || theme.colors.black};
  p {
    text-shadow: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 100%;
  }
  letter-spacing: ${({ $letterSpacing }) => $letterSpacing + "px"};
  line-height: ${({ $rowSpacing }) => $rowSpacing};
  a {
    color: ${({ $linkColor }) => $linkColor};
  }
`;

const SideBySideHero = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  gap: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    grid-template-columns: 1fr;
    grid-template-rows: ${({ $imagePlace }) =>
      $imagePlace === "left" ? "75vw 1fr" : "1fr 75vw"};
  }
`;
const SideBySideCarouselWrapper = styled.div`
  width: 100%;
`;
const CarouselChildren = styled(HeroContent)`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  place-content: center;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  align-items: ${({ $alignContent }) => $alignContent || "center"};
  height: 100%;
  width: 100%;
  padding-inline: 3rem;
`;
const CarouselChildrenInnerWrapper = styled.div`
  max-width: 75%;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 65%;
  }
`;
const SideBySideMediaWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const wrappers = {
  big: BigHero,
  small: SmHero,
  frontpage: FrontpageHero,
};

export default function Hero({
  media,
  mobileMedia,
  variant,
  children,
  mediaItems,
  mediaItemsMobile,
  backgroundColorHex,
  textColorHex,
  alignContent,
  letterSpacing,
  rowSpacing,
  linkColor,
  imagePlace,
}) {
  if (variant === "side-by-side" && imagePlace === "left") {
    return (
      <SideBySideHero $imagePlace={imagePlace}>
        <SideBySideMediaWrapper>
          {mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
            <SideBySideCarouselWrapper>
              <HeroCarousel
                mediaItems={mediaItems}
                mediaItemsMobile={mediaItemsMobile}
              />
            </SideBySideCarouselWrapper>
          ) : (
            <MediaWithArtDirection
              media={media}
              mobileMedia={mobileMedia}
              loading="eager"
            />
          )}
        </SideBySideMediaWrapper>
        <SideBySideContent
          backgroundColor={backgroundColorHex}
          textColor={textColorHex}
          $letterSpacing={letterSpacing}
          $rowSpacing={rowSpacing}
          $linkColor={linkColor}
        >
          {children}
        </SideBySideContent>
      </SideBySideHero>
    );
  } else if (variant === "side-by-side" && imagePlace === "right") {
    return (
      <SideBySideHero>
        <SideBySideContent
          backgroundColor={backgroundColorHex}
          textColor={textColorHex}
          $letterSpacing={letterSpacing}
          $rowSpacing={rowSpacing}
          $linkColor={linkColor}
        >
          {children}
        </SideBySideContent>
        <SideBySideMediaWrapper>
          {mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
            <SideBySideCarouselWrapper>
              <HeroCarousel
                mediaItems={mediaItems}
                mediaItemsMobile={mediaItemsMobile}
              />
            </SideBySideCarouselWrapper>
          ) : (
            <MediaWithArtDirection
              media={media}
              mobileMedia={mobileMedia}
              loading="eager"
            />
          )}
        </SideBySideMediaWrapper>
      </SideBySideHero>
    );
  }
  const Wrapper = wrappers[variant];
  return (
    <Wrapper>
      {mediaItems?.length > 0 || mediaItemsMobile?.length > 0 ? (
        <>
          <HeroCarousel
            mediaItems={mediaItems}
            mediaItemsMobile={mediaItemsMobile}
          ></HeroCarousel>
          <CarouselChildren $alignContent={alignContent}>
            <CarouselChildrenInnerWrapper>
              {children}
            </CarouselChildrenInnerWrapper>
          </CarouselChildren>
        </>
      ) : (
        <>
          <MediaWithArtDirection
            media={media}
            mobileMedia={mobileMedia}
            loading="eager"
          />
          <HeroContent $letterSpacing={letterSpacing} $rowSpacing={rowSpacing}>
            {children}
          </HeroContent>
        </>
      )}
    </Wrapper>
  );
}

Hero.propTypes = {
  mediaUrl: PropTypes.object,
  mediaAlt: PropTypes.string,
  media: PropTypes.object,
  mobileMedia: PropTypes.object,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["big", "frontpage", "small", "side-by-side"]),
  mediaItems: PropTypes.arrayOf(PropTypes.object),
  mediaItemsMobile: PropTypes.arrayOf(PropTypes.object),
  backgroundColorHex: PropTypes.string,
  textColorHex: PropTypes.string,
  alignContent: PropTypes.string,
  letterSpacing: PropTypes.string,
  rowSpacing: PropTypes.string,
  linkColor: PropTypes.string,
  imagePlace: PropTypes.string,
};

Hero.defaultProps = {
  children: undefined,
  mediaUrl: undefined,
  mediaAlt: undefined,
  media: undefined,
  mobileMedia: undefined,
  variant: "big",
  mediaItems: undefined,
  mediaItemsMobile: undefined,
};
